import { type GetExamCenterEventSummariesQuery } from '@exam-center/graphql';
import { dayjs } from '@shared/utils';

import { type ExamEventStatsPeriod } from '../../hooks';

export function getFilterParamsByExamEventStatsPeriod(period: ExamEventStatsPeriod) {
  switch (period) {
    case 'current-month':
      return {
        startedAt: dayjs().startOf('month').toDate(),
        endedAt: dayjs().endOf('month').toDate(),
      };
    case 'last-3-months':
      return {
        startedAt: dayjs().subtract(3, 'month').startOf('month').toDate(),
        endedAt: dayjs().endOf('month').toDate(),
      };
    case 'last-6-months':
      return {
        startedAt: dayjs().subtract(6, 'month').startOf('month').toDate(),
        endedAt: dayjs().endOf('month').toDate(),
      };
    default: {
      const yearDate = dayjs().set('year', period);

      return {
        startedAt: yearDate.startOf('year').toDate(),
        endedAt: yearDate.endOf('year').toDate(),
      };
    }
  }
}

export function getExamEventStatsSummaryData(
  examCenterEventSummariesData: GetExamCenterEventSummariesQuery | undefined,
) {
  const summaryData =
    examCenterEventSummariesData?.ecGetExamCenterEventSummaries?.examCenterEventSummaries;

  const {
    publicRoundCount = 0,
    inHouseRoundCount = 0,
    publicSeatCount = 0,
    inHouseSeatCount = 0,
    publicExamEventExamineeCount = 0,
    inHouseExamEventExamineeCount = 0,
    publicAttendedExamineeCount = 0,
    inHouseAttendedExamineeCount = 0,
    publicAbsentExamineeCount = 0,
    inHouseAbsentExamineeCount = 0,
    publicCanceledExamineeCount = 0,
    inHouseCanceledExamineeCount = 0,
  } = summaryData ?? {};
  // TODO: Add separated canceled examinee count when the ui for canceled examinee is ready. For now, we will count canceled examinee as absent examinee.
  return {
    roundCount: publicRoundCount + inHouseRoundCount,
    publicRoundCount: publicRoundCount,
    inHouseRoundCount: inHouseRoundCount,
    seatCount: publicSeatCount + inHouseSeatCount,
    publicSeatCount: publicSeatCount,
    inHouseSeatCount: inHouseSeatCount,
    examineeCount: publicExamEventExamineeCount + inHouseExamEventExamineeCount,
    publicExamEventExamineeCount: publicExamEventExamineeCount,
    inHouseExamEventExamineeCount: inHouseExamEventExamineeCount,
    attendedExamineeCount: publicAttendedExamineeCount + inHouseAttendedExamineeCount,
    publicAttendedExamineeCount: publicAttendedExamineeCount,
    inHouseAttendedExamineeCount: inHouseAttendedExamineeCount,
    absentExamineeCount:
      publicAbsentExamineeCount +
      inHouseAbsentExamineeCount +
      publicCanceledExamineeCount +
      inHouseCanceledExamineeCount,
    publicAbsentExamineeCount: publicAbsentExamineeCount + publicCanceledExamineeCount,
    inHouseAbsentExamineeCount: inHouseAbsentExamineeCount + inHouseCanceledExamineeCount,
  };
}

function formatDateRangeTitle(monthsToSubtract: number): string {
  const now = dayjs();
  const startDate = now.subtract(monthsToSubtract, 'month');
  const endDate = now;

  const startPattern = startDate.isSame(endDate, 'year') ? 'MMMM' : 'MMMM YYYY';

  const startPeriod = startDate.format(startPattern);
  const endPeriod = endDate.format('MMMM YYYY');
  return `ข้อมูลช่วง ${startPeriod} - ${endPeriod}`;
}

export function getExamEventStatsSummaryTitle(period: ExamEventStatsPeriod) {
  const now = dayjs();

  switch (period) {
    case 'current-month': {
      const currentMonthAndYear = now.format('MMMM YYYY');
      return `ข้อมูลสรุปประจำเดือน ${currentMonthAndYear}`;
    }
    case 'last-3-months': {
      return formatDateRangeTitle(3);
    }
    case 'last-6-months': {
      return formatDateRangeTitle(6);
    }
    default: {
      const year = now.set('year', period).format('YYYY');
      return `ข้อมูลสรุปประจำปี ${year}`;
    }
  }
}
